import appManager from '@app/appManager';
import DiagnosticsModule from '@modules/DiagnosticsModule';
import FeatureFlagModule from '@modules/FeatureFlagModule';
import { FeatureFlagKeys } from '@modules/FeatureFlagModule/featureFlagKeys';

const TRACE_ID_HEADER = 'X-Trace-Id';
const REQUEST_DURATION_HEADER = 'X-Request-Duration-Ms';

// How long to wait for the matching PerformanceResourceTiming entry to land
// before giving up on reporting response size for this request.
const RESOURCE_TIMING_MATCH_TIMEOUT_MS = 5000;

function resolveRequestUrl(input: RequestInfo | URL): string {
  if (typeof input === 'string') {
    return new URL(input, window.location.origin).toString();
  }
  if (input instanceof URL) {
    return input.toString();
  }
  return input.url;
}

interface ResourceTimingObservation {
  match: Promise<PerformanceResourceTiming | undefined>;
  disconnect: () => void;
}

/**
 * Starts observing for the PerformanceResourceTiming entry matching this
 * fetch call. Returns a promise that resolves once the entry lands (never
 * rejects), plus a `disconnect` for callers to stop observing early.
 *
 * Uses a PerformanceObserver with `buffered: true` rather than polling
 * `performance.getEntriesByType('resource')` after the fact, since the
 * resource timing buffer is capped (~150-250 entries) and entries can be
 * evicted under load before a poll would see them. Matched by request URL
 * and a timing window (the entry must start at or after this call started).
 *
 * `encodedBodySize`/`decodedBodySize` (and other size fields) only populate
 * for same-origin resources, or cross-origin ones whose response sets
 * `Timing-Allow-Origin` for this origin — otherwise they read as 0. GraphQL
 * requests go to `API_URL`, a different origin than the app itself, so
 * these currently read 0 until the BFF adds that header.
 */
function observeResourceTiming(
  url: string,
  startPerf: number,
): ResourceTimingObservation {
  let observer: PerformanceObserver | undefined;

  const match = new Promise<PerformanceResourceTiming | undefined>(
    (resolve) => {
      try {
        observer = new PerformanceObserver((list) => {
          const entry = list
            .getEntries()
            .find(
              (candidate) =>
                candidate.name === url && candidate.startTime >= startPerf,
            ) as PerformanceResourceTiming | undefined;

          if (entry) {
            resolve(entry);
          }
        });
        observer.observe({ type: 'resource', buffered: true });
      } catch {
        // PerformanceObserver unsupported, or 'resource' entry type
        // unavailable — fail silently, size reporting is best-effort.
        resolve(undefined);
      }
    },
  );

  return { match, disconnect: () => observer?.disconnect() };
}

/**
 * Races a resource-timing match against a timeout, disconnecting the
 * observer either way. Called once the fetch itself has settled, so the
 * timeout bounds only the wait for the entry to land — not the request's
 * own duration. Starting it earlier (e.g. alongside the fetch) would let
 * slow-but-successful requests exceed the timeout before their entry could
 * ever be created, silently dropping size metrics for exactly the
 * largest/slowest responses this feature cares about.
 */
function withResourceTimingTimeout(
  observation: ResourceTimingObservation,
  timeoutMs: number,
): Promise<PerformanceResourceTiming | undefined> {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      observation.disconnect();
      resolve(undefined);
    }, timeoutMs);

    observation.match.then((entry) => {
      clearTimeout(timeoutId);
      observation.disconnect();
      resolve(entry);
    });
  });
}

/**
 * Starts observing for response size, or returns `undefined` when the
 * feature is off — creating a PerformanceObserver + arming a timeout on
 * every single GraphQL call adds up, since loggingFetch runs on every
 * request regardless of flag state.
 *
 * Gated behind its own flag rather than `featureNetworkMonitoringEnabled`
 * (which already gates latency reporting): this code path carries more
 * risk (a PerformanceObserver + timeout per request, versus reading an
 * existing response header) and should be independently killable without
 * touching the already-stable latency reporting it shares a file with.
 */
function startResourceTimingObservationIfEnabled(
  input: RequestInfo | URL,
  startPerf: number,
): ResourceTimingObservation | undefined {
  if (
    !FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureNetworkCompressionMonitoringEnabled,
    )
  ) {
    return undefined;
  }

  return observeResourceTiming(resolveRequestUrl(input), startPerf);
}

/**
 * Reports response size once the matching resource-timing entry resolves,
 * gated behind the resource-timing observation existing (the feature flag
 * was off) and the same tab-visibility check as latency reporting (checked
 * again here since it can change while the request is in flight). A missed
 * match (evicted buffer, unsupported API, timeout) fails silently — this
 * must never affect the caller's response or other reporting.
 */
function reportResponseSizeWhenReady(
  resourceTiming: ResourceTimingObservation | undefined,
  operationName: string,
  timestamp: string,
  tabVisible: boolean,
): void {
  if (!resourceTiming) {
    return;
  }

  if (!tabVisible) {
    // Not consuming the match — disconnect now rather than leaking the
    // observer (withResourceTimingTimeout, which normally owns this, is
    // never reached below).
    resourceTiming.disconnect();
    return;
  }

  withResourceTimingTimeout(
    resourceTiming,
    RESOURCE_TIMING_MATCH_TIMEOUT_MS,
  ).then((entry) => {
    if (!entry) {
      appManager
        .logger('api')
        .debug(`no matching resource timing entry for '${operationName}'`);
      return;
    }

    DiagnosticsModule.reportResponseSize({
      encodedBodySize: entry.encodedBodySize,
      decodedBodySize: entry.decodedBodySize,
      operationName,
      timestamp,
    });
  });
}

/**
 * Handles a failed fetch call: distinguishes a user/navigation-triggered
 * abort (debug-logged, no failure event) from a genuine network failure
 * (error-logged, emits `network.request.failed`), then re-throws either
 * way so the caller's rejection is unchanged.
 */
function handleFetchFailure(
  error: unknown,
  operationName: string,
  duration: number,
  timestamp: string,
): never {
  if (error instanceof DOMException && error.name === 'AbortError') {
    // AbortError = page refresh or user cancel — not a network failure
    appManager
      .logger('api')
      .debug(`request for '${operationName}' was aborted`, { operationName });
    throw error;
  }

  // TypeError = network failure (DNS, connection refused, timeout)
  appManager.logger('api').error(`network failure for '${operationName}'`, {
    operationName,
    duration,
    error: error instanceof Error ? error.message : String(error),
  });

  appManager.events.emit('network.request.failed', {
    operationName,
    duration,
    timestamp,
    errorType: 'NetworkFailure',
  });

  throw error;
}

interface LatencyMetrics {
  frontendRequestDuration: number;
  backendDuration: number | undefined;
  networkLatency: number | undefined;
}

/** Derives the frontend/backend/network latency figures for a response. */
function computeLatencyMetrics(
  startPerf: number,
  response: Response,
): LatencyMetrics {
  const frontendRequestDuration = performance.now() - startPerf;
  const backendDurationMs = response.headers.get(REQUEST_DURATION_HEADER);
  const backendDuration = backendDurationMs
    ? parseInt(backendDurationMs, 10)
    : undefined;
  const networkLatency =
    backendDuration !== undefined
      ? frontendRequestDuration - backendDuration
      : undefined;

  return { frontendRequestDuration, backendDuration, networkLatency };
}

/**
 * Reports network latency once gated behind the feature flag, tab
 * visibility, and both durations actually being known.
 */
function reportNetworkLatencyIfReady(
  metrics: LatencyMetrics,
  operationName: string,
  timestamp: string,
  tabVisible: boolean,
): void {
  if (
    !FeatureFlagModule.isEnabled(
      FeatureFlagKeys.featureNetworkMonitoringEnabled,
    ) ||
    !tabVisible ||
    metrics.networkLatency === undefined ||
    metrics.backendDuration === undefined
  ) {
    return;
  }

  DiagnosticsModule.reportNetworkLatency({
    networkLatencyMs: metrics.networkLatency,
    frontendRequestDurationMs: metrics.frontendRequestDuration,
    backendProcessingDurationMs: metrics.backendDuration,
    operationName,
    timestamp,
  });
}

/**
 * Performs a fetch request and logs the request and response details.
 *
 * @param input - The resource that you wish to fetch. This can either be a
 * string containing the URL of the resource, or a `Request` object.
 * @param init - An optional object containing any custom settings that you
 * want to apply to the request.
 * @returns A `Promise` that resolves to a `Response` object, with an
 * overridden `text` method that logs errors if present.
 *
 * The function logs the following details:
 * - The operation name from the request body.
 * - The frontend request duration (total time from FE perspective).
 * - The backend processing duration (from X-Request-Duration-Ms header).
 * - The network latency (FE duration - BE duration).
 * - The trace ID from the response headers.
 * - The variables from the request body.
 * - Any errors present in the response.
 * - Whether the browser tab was visible during the request.
 * - The on-wire (encoded) and decoded response sizes, once the matching
 *   `PerformanceResourceTiming` entry lands (best-effort, reported
 *   asynchronously and never affects the returned response).
 */
async function loggingFetch(
  input: RequestInfo | URL,
  init?: RequestInit,
): Promise<Response> {
  let body: { operationName?: string; variables?: unknown } = {};
  let operationName = '';

  if (init?.body && typeof init.body === 'string') {
    body = JSON.parse(init.body);
    operationName = body.operationName ?? '';
  } else {
    operationName = 'FileUpload';
  }

  // Use performance.now() for monotonic timing (unaffected by NTP/clock changes)
  const startPerf = performance.now();
  // Use Date.now() only for wall-clock timestamps in logs and events
  const timestamp = new Date().toISOString();
  // Start observing before the fetch (when enabled) so the entry is caught
  // live as it lands, rather than relying solely on the buffered replay
  // afterwards.
  const resourceTiming = startResourceTimingObservationIfEnabled(
    input,
    startPerf,
  );

  let response: Response;
  try {
    response = await fetch(input, init);
  } catch (error) {
    resourceTiming?.disconnect();
    handleFetchFailure(
      error,
      operationName,
      performance.now() - startPerf,
      timestamp,
    );
  }

  const metrics = computeLatencyMetrics(startPerf, response);
  const tabVisible = !document.hidden;

  appManager
    .logger('api')
    .info(
      `request for '${operationName}' in ${metrics.frontendRequestDuration}ms`,
      {
        traceId: response.headers.get(TRACE_ID_HEADER),
        variables: body.variables,
        frontendRequestDurationMs: metrics.frontendRequestDuration,
        backendProcessingDurationMs: metrics.backendDuration,
        networkLatencyMs: metrics.networkLatency,
        tabVisible,
      },
    );

  if (!tabVisible) {
    appManager.logger('api').debug('Skipping latency - tab was hidden');
  }

  reportNetworkLatencyIfReady(metrics, operationName, timestamp, tabVisible);

  // Fire-and-forget: resolving the matching resource-timing entry must
  // never delay or affect the response returned to the caller. The timeout
  // starts here (after fetch settled), not when observation began.
  reportResponseSizeWhenReady(
    resourceTiming,
    operationName,
    timestamp,
    tabVisible,
  );

  // Store the original text method
  const originalText = response.text.bind(response);

  // Override the text method to add error logging
  response.text = async () => {
    const resultText = await originalText();

    try {
      const result = JSON.parse(resultText);

      if (result.errors) {
        appManager
          .logger('api')
          .error(`request for '${operationName}' failed`, {
            traceId: response.headers.get(TRACE_ID_HEADER),
            variables: body.variables,
            errors: result.errors,
          });
      }
    } catch (error) {
      appManager
        .logger('api')
        .error(`Failed to parse response for '${operationName}'`, {
          error: error instanceof Error ? error.message : String(error),
          traceId: response.headers.get(TRACE_ID_HEADER),
          variables: body.variables,
        });
    }

    return resultText;
  };

  return response;
}

export default loggingFetch;
